import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useLarguraTela } from '../hooks/useLarguraTela';

const LOG_COLORS = {
  info:    '#64748b',
  command: '#2563eb',
  stdout:  '#334155',
  stderr:  '#b45309',
  success: '#15803d',
  warn:    '#d97706',
  error:   '#dc2626',
};

const STATUS = {
  success:   { bg: '#f0fdf4', border: '#86efac', color: '#15803d', label: 'Sucesso' },
  failed:    { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c', label: 'Falha' },
  deploying: { bg: '#fffbeb', border: '#fcd34d', color: '#b45309', label: 'Em andamento' },
};

const TYPE_LABELS = { react: 'React', aspnet: 'ASP.NET', node: 'Node.js' };

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatarSolicitante(requestedBy) {
  if (!requestedBy) return 'Não informado';
  if (typeof requestedBy === 'string') return requestedBy;
  return requestedBy.nomeCompleto || requestedBy.login || 'Não informado';
}

const S = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.6)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
  },
  modal: {
    background: '#ffffff',
    width: '100%',
    maxWidth: 1100,
    margin: '24px auto',
    borderRadius: 12,
    border: '1px solid #dbe4ef',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    background: '#ffffff',
    borderBottom: '1px solid #dbe4ef',
    padding: '0 24px',
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    gap: 10,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  modalTitle: {
    color: '#0f172a',
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: '-0.2px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  closeBtn: {
    padding: '7px 16px',
    background: 'transparent',
    color: '#334155',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    fontSize: 13,
    cursor: 'pointer',
    flexShrink: 0,
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  sidebar: {
    width: 220,
    flexShrink: 0,
    borderRight: '1px solid #dbe4ef',
    overflowY: 'auto',
    background: '#f8fafc',
  },
  sidebarTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '14px 16px 6px',
  },
  projectItem: (selected) => ({
    padding: '10px 12px 10px 16px',
    cursor: 'pointer',
    borderLeft: selected ?'3px solid #3b82f6' : '3px solid transparent',
    background: selected ?'#eff6ff' : 'transparent',
    transition: 'background 0.15s',
  }),
  projectName: {
    color: '#0f172a',
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  projectType: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 2,
  },
  deployList: {
    width: 300,
    flexShrink: 0,
    borderRight: '1px solid #dbe4ef',
    overflowY: 'auto',
    background: '#ffffff',
  },
  deployListTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '14px 16px 6px',
    borderBottom: '1px solid #dbe4ef',
  },
  deployItem: (selected) => ({
    padding: '10px 14px',
    cursor: 'pointer',
    borderLeft: selected ?'3px solid #3b82f6' : '3px solid transparent',
    background: selected ?'#eff6ff' : '#ffffff',
    borderBottom: '1px solid #dbe4ef',
    transition: 'background 0.15s',
  }),
  deployDate: {
    color: '#64748b',
    fontSize: 12,
    marginBottom: 4,
  },
  deployRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  statusBadge: (status) => ({
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4,
    background: STATUS[status]?.bg || '#f8fafc',
    color: STATUS[status]?.color || '#64748b',
    border: `1px solid ${STATUS[status]?.border || '#cbd5e1'}`,
  }),
  duration: {
    color: '#64748b',
    fontSize: 11,
  },
  requestedBy: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 4,
  },
  errorPreview: {
    color: '#dc2626',
    fontSize: 11,
    marginTop: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 260,
  },
  logPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#ffffff',
  },
  logHeader: {
    padding: '10px 20px',
    borderBottom: '1px solid #dbe4ef',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
    flexWrap: 'wrap',
    background: '#f8fafc',
  },
  logHeaderTitle: {
    color: '#0f172a',
    fontSize: 12,
    fontWeight: 600,
    width: '100%',
  },
  logMeta: {
    color: '#64748b',
    fontSize: 12,
  },
  logBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 20px',
    fontFamily: "'Consolas', 'Cascadia Code', 'Fira Code', monospace",
    fontSize: 12,
    lineHeight: 1.65,
    background: '#ffffff',
  },
  logLine: (level) => ({
    color: LOG_COLORS[level] || '#64748b',
    marginBottom: 2,
    wordBreak: 'break-all',
  }),
  logTimestamp: {
    color: '#94a3b8',
    marginRight: 8,
    fontSize: 11,
    userSelect: 'none',
  },
  emptyState: {
    color: '#64748b',
    textAlign: 'center',
    padding: 32,
    fontSize: 13,
  },
  loadingState: {
    color: '#64748b',
    textAlign: 'center',
    padding: 24,
    fontSize: 13,
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid #dbe4ef',
    background: '#ffffff',
    flexShrink: 0,
  },
  tabBotao: (ativo) => ({
    flex: 1,
    padding: '13px 8px',
    minHeight: 44,
    background: 'none',
    border: 'none',
    borderBottom: `2px solid ${ativo ?'#3b82f6' : 'transparent'}`,
    color: ativo ?'#2563eb' : '#64748b',
    fontWeight: ativo ?700 : 500,
    fontSize: 13,
    cursor: 'pointer',
  }),
};

export default function LogsViewer({ token, onClose, projectIdInicial = null }) {
  const api = useApi(token);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deploys, setDeploys] = useState([]);
  const [selectedDeploy, setSelectedDeploy] = useState(null);
  const [logDetail, setLogDetail] = useState(null);
  const [loadingDeploys, setLoadingDeploys] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('projetos');
  const logEndRef = useRef(null);
  const larguraTela = useLarguraTela();
  const ehMobile = larguraTela <= 900;
  const ehTablet = larguraTela <= 1200;

  useEffect(() => {
    api.getProjects().then(setProjects).catch(console.error);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (projects.length === 0) return;

    if (projectIdInicial) {
      const projetoEncontrado = projects.find((projeto) => projeto.id === projectIdInicial);
      if (projetoEncontrado) {
        setSelectedProject(projetoEncontrado);
        return;
      }
    }

    setSelectedProject((projetoAtual) => {
      if (projetoAtual && projects.some((projeto) => projeto.id === projetoAtual.id)) {
        return projetoAtual;
      }
      return projects[0];
    });
  }, [projects, projectIdInicial]);

  useEffect(() => {
    if (!selectedProject) return;
    setLoadingDeploys(true);
    setDeploys([]);
    setSelectedDeploy(null);
    setLogDetail(null);

    api.getProjectLogs(selectedProject.id)
      .then((dadosDeploy) => {
        setDeploys(dadosDeploy);
        if (dadosDeploy.length > 0) {
          setSelectedDeploy(dadosDeploy[0]);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingDeploys(false));
  }, [selectedProject]); // eslint-disable-line

  useEffect(() => {
    if (!selectedProject || !selectedDeploy) return;
    setLoadingDetail(true);
    setLogDetail(null);

    api.getDeployLogDetail(selectedProject.id, selectedDeploy.id)
      .then((dadosDetalhe) => {
        setLogDetail(dadosDetalhe);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      })
      .catch(console.error)
      .finally(() => setLoadingDetail(false));
  }, [selectedDeploy, selectedProject]); // eslint-disable-line

  const handleSelecionarProjeto = (p) => {
    setSelectedProject(p);
    if (ehMobile) setAbaAtiva('deploys');
  };

  const handleSelecionarDeploy = (d) => {
    setSelectedDeploy(d);
    if (ehMobile) setAbaAtiva('logs');
  };

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div style={S.overlay} onClick={handleOverlayClick}>
      <div
        style={{
          ...S.modal,
          margin: ehMobile ?0 : S.modal.margin,
          borderRadius: ehMobile ?0 : S.modal.borderRadius,
          maxWidth: ehMobile ?'100%' : S.modal.maxWidth,
          height: ehMobile ?'100dvh' : 'auto',
        }}
      >
        <div style={{ ...S.modalHeader, padding: ehMobile ?'0 12px' : S.modalHeader.padding }}>
          <div style={S.modalTitle}>
            <span>Histórico de Builds</span>
          </div>
          <button style={S.closeBtn} onClick={onClose}>Fechar</button>
        </div>

        {ehMobile && (
          <div style={S.tabBar}>
            <button style={S.tabBotao(abaAtiva === 'projetos')} onClick={() => setAbaAtiva('projetos')}>
              Projetos
            </button>
            <button style={S.tabBotao(abaAtiva === 'deploys')} onClick={() => setAbaAtiva('deploys')}>
              Deploys
            </button>
            <button style={S.tabBotao(abaAtiva === 'logs')} onClick={() => setAbaAtiva('logs')}>
              Logs
            </button>
          </div>
        )}

        <div style={{ ...S.body, flexDirection: ehTablet ?'column' : 'row' }}>
          <div
            style={{
              ...S.sidebar,
              width: ehTablet ?'100%' : S.sidebar.width,
              maxHeight: ehMobile ?'none' : ehTablet ?170 : 'none',
              flex: ehMobile ?1 : 'none',
              borderRight: ehTablet ?'none' : S.sidebar.borderRight,
              borderBottom: ehTablet && !ehMobile ?'1px solid #dbe4ef' : 'none',
              display: ehMobile && abaAtiva !== 'projetos' ?'none' : 'block',
            }}
          >
            <div style={S.sidebarTitle}>Projetos</div>
            {projects.length === 0 ?(
              <div style={S.emptyState}>Nenhum projeto</div>
            ) : (
              projects.map((p) => (
                <div
                  key={p.id}
                  style={S.projectItem(selectedProject?.id === p.id)}
                  onClick={() => handleSelecionarProjeto(p)}
                >
                  <div style={S.projectName}>{p.name}</div>
                  <div style={S.projectType}>{TYPE_LABELS[p.type] || p.type}</div>
                </div>
              ))
            )}
          </div>

          <div
            style={{
              ...S.deployList,
              width: ehTablet ?'100%' : S.deployList.width,
              maxHeight: ehMobile ?'none' : ehTablet ?260 : 'none',
              flex: ehMobile ?1 : 'none',
              borderRight: ehTablet ?'none' : S.deployList.borderRight,
              borderBottom: ehTablet && !ehMobile ?'1px solid #dbe4ef' : 'none',
              display: ehMobile && abaAtiva !== 'deploys' ?'none' : 'block',
            }}
          >
            <div style={S.deployListTitle}>
              {selectedProject ? `Deploys - ${selectedProject.name}` : 'Selecione um projeto'}
            </div>

            {!selectedProject ?(
              <div style={S.emptyState}>Selecione um projeto</div>
            ) : loadingDeploys ?(
              <div style={S.loadingState}>Carregando...</div>
            ) : deploys.length === 0 ?(
              <div style={S.emptyState}>Nenhum build registrado</div>
            ) : (
              deploys.map((d) => (
                <div
                  key={d.id}
                  style={S.deployItem(selectedDeploy?.id === d.id)}
                  onClick={() => handleSelecionarDeploy(d)}
                >
                  <div style={S.deployDate}>{formatDate(d.timestamp)}</div>
                  <div style={S.deployRow}>
                    <span style={S.statusBadge(d.status)}>
                      {STATUS[d.status]?.label || d.status}
                    </span>
                    <span style={S.duration}>{d.duration || '-'}</span>
                  </div>
                  <div style={S.requestedBy}>
                    Solicitante: {formatarSolicitante(d.requestedBy)}
                  </div>
                  {d.status === 'failed' && d.error && (
                    <div style={S.errorPreview} title={d.error}>
                      {d.error}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div style={{ ...S.logPanel, display: ehMobile && abaAtiva !== 'logs' ?'none' : 'flex' }}>
            {!selectedDeploy ?(
              <div style={{ ...S.emptyState, paddingTop: 80 }}>
                Selecione um deploy para ver os logs
              </div>
            ) : (
              <>
                <div style={{ ...S.logHeader, padding: ehMobile ?'10px 12px' : S.logHeader.padding }}>
                  <span style={S.logHeaderTitle}>
                    {formatDate(selectedDeploy.timestamp)}
                  </span>
                  <span style={S.logMeta}>
                    Solicitante: {formatarSolicitante(logDetail?.requestedBy || selectedDeploy.requestedBy)}
                  </span>
                  <span style={S.logMeta}>
                    Duração: {logDetail?.duration || selectedDeploy.duration || '-'}
                  </span>
                  <span style={S.statusBadge(selectedDeploy.status)}>
                    {STATUS[selectedDeploy.status]?.label || selectedDeploy.status}
                  </span>
                </div>

                <div style={{ ...S.logBody, padding: ehMobile ?'10px 12px' : S.logBody.padding }}>
                  {loadingDetail ?(
                    <div style={S.loadingState}>Carregando logs...</div>
                  ) : logDetail ?(
                    <>
                      {logDetail.logs.map((entry, i) => (
                        <div key={i} style={S.logLine(entry.level)}>
                          <span style={S.logTimestamp}>
                            {new Date(entry.timestamp).toLocaleTimeString('pt-BR')}
                          </span>
                          {entry.message}
                        </div>
                      ))}
                      {logDetail.error && (
                        <div
                          style={{
                            ...S.logLine('error'),
                            marginTop: 12,
                            padding: '8px 12px',
                            background: '#fef2f2',
                            borderRadius: 6,
                            borderLeft: '3px solid #b91c1c',
                          }}
                        >
                          <strong>Erro:</strong> {logDetail.error}
                        </div>
                      )}
                      <div ref={logEndRef} />
                    </>
                  ) : (
                    <div style={S.emptyState}>Nenhum log encontrado</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

